import React, { Component } from 'react';
import { Modal, Tree, Spin, message } from 'antd';
import $ from '../../../common/AjaxRequest';
import API_URL from '../../../common/url';

const TreeNode = Tree.TreeNode;

/**
 * 部门文件/文件夹移动 弹框组件（部门内移动，非迁移到项目）
 * @ClassName MoveFolderModal
 * @Author nrj
 * @Date 2026/7/9
 */
class MoveFolderModal extends Component {

    state = {
        visible: false,
        loading: false,
        treeLoading: false,
        treeData: [],           // 筛选后的目录树（仅显示OWNER目录）
        rawTreeData: [],        // 原始目录树
        expandedKeys: [],
        autoExpandParent: true,
        selectedKeys: [],
        selectedCatalogId: null,
        // 移动类型：'folder' 移动文件夹 | 'file' 移动文件
        mode: 'folder',
        // 要移动的源ID：目录ID或文件ID
        sourceId: null,
        sourceName: '',
    };

    /**
     * 显示弹框（移动文件夹）
     */
    showMoveFolder = (catalogId, catalogName) => {
        this.setState({
            visible: true,
            mode: 'folder',
            sourceId: catalogId,
            sourceName: catalogName,
            selectedCatalogId: null,
            selectedKeys: [],
            expandedKeys: [],
            autoExpandParent: true,
        }, () => {
            this.loadTreeData();
        });
    };

    /**
     * 显示弹框（移动文件）
     */
    showMoveFile = (fileId, fileName) => {
        this.setState({
            visible: true,
            mode: 'file',
            sourceId: fileId,
            sourceName: fileName,
            selectedCatalogId: null,
            selectedKeys: [],
            expandedKeys: [],
            autoExpandParent: true,
        }, () => {
            this.loadTreeData();
        });
    };

    /**
     * 加载部门目录树，过滤出仅有OWNER权限的目录
     */
    loadTreeData = () => {
        this.setState({ treeLoading: true });
        const options = {
            method: 'POST',
            url: API_URL.deptFile.queryFileCatalog,
            data: {},
            dataType: 'json',
            doneResult: res => {
                const { catalogs = [] } = res;
                // 过滤：只保留OWNER权限的目录层级
                const filteredTree = this.filterOwnerNodes(catalogs);
                this.setState({
                    rawTreeData: catalogs,
                    treeData: filteredTree,
                    treeLoading: false,
                });
            },
            errorResult: () => {
                this.setState({ treeLoading: false });
                message.error('加载目录树失败');
            }
        };
        $.sendRequest(options);
    };

    /**
     * 递归过滤目录树，仅保留OWNER权限节点
     * 非OWNER节点若有OWNER子节点则保留作为容器（不可选中）
     */
    filterOwnerNodes = (nodes) => {
        if (!nodes || nodes.length === 0) return [];
        return nodes
            .filter(node => {
                if (node.premission === 'OWNER') return true;
                if (node.children && node.children.length > 0) {
                    const filteredChildren = this.filterOwnerNodes(node.children);
                    return filteredChildren.length > 0;
                }
                return false;
            })
            .map(node => {
                const newNode = { ...node };
                if (node.children && node.children.length > 0) {
                    newNode.children = this.filterOwnerNodes(node.children);
                } else {
                    newNode.children = [];
                }
                return newNode;
            });
    };

    /**
     * 展开/收起节点
     */
    onExpand = (expandedKeys) => {
        this.setState({
            expandedKeys,
            autoExpandParent: false,
        });
    };

    /**
     * 选择目录节点（仅允许选中OWNER权限的目录）
     */
    onSelect = (selectedKeys, info) => {
        if (selectedKeys.length === 0) {
            this.setState({
                selectedKeys: [],
                selectedCatalogId: null,
            });
            return;
        }
        // 检查选中节点的权限
        const node = info.node;
        const isOwner = node.props.premission === 'OWNER';
        if (!isOwner) {
            message.warning('请选择有Owner权限的目录');
            return;
        }
        this.setState({
            selectedKeys,
            selectedCatalogId: Number(selectedKeys[0]),
        });
    };

    /**
     * 确认移动
     */
    handleOk = () => {
        const { mode, sourceId, selectedCatalogId } = this.state;

        if (!sourceId) {
            message.error('源数据ID不能为空');
            return;
        }
        if (!selectedCatalogId) {
            message.warning('请选择目标目录');
            return;
        }

        Modal.confirm({
            title: '确认移动',
            content: '确定要移动' + (mode === 'folder' ? '文件夹' : '文件') + '到所选目录吗？',
            onOk: () => {
                this.doMove();
            },
        });
    };

    /**
     * 执行移动请求
     */
    doMove = () => {
        const { mode, sourceId, selectedCatalogId } = this.state;
        this.setState({ loading: true });

        const url = mode === 'folder' ? API_URL.deptFile.moveFolder : API_URL.deptFile.moveFile;
        const data = mode === 'folder'
            ? { catalogId: sourceId, targetCatalogId: selectedCatalogId }
            : { fileId: sourceId, targetCatalogId: selectedCatalogId };

        const options = {
            method: 'POST',
            url,
            data,
            dataType: 'json',
            doneResult: res => {
                this.setState({ loading: false });
                message.success(res.message || '移动成功');
                this.handleCancel();
                if (this.props.onSuccess) {
                    this.props.onSuccess();
                }
            },
            errorResult: (err) => {
                this.setState({ loading: false });
                message.error(err && err.message ? err.message : '移动失败');
            }
        };
        $.sendRequest(options);
    };

    /**
     * 关闭弹框
     */
    handleCancel = () => {
        this.setState({
            visible: false,
            treeData: [],
            rawTreeData: [],
            selectedCatalogId: null,
            selectedKeys: [],
            expandedKeys: [],
            sourceId: null,
            sourceName: '',
            loading: false,
        });
    };

    /**
     * 渲染目录树节点（仅允许选中OWNER节点）
     */
    renderTreeNodes = (data) => {
        if (!data || data.length === 0) return null;
        return data.map(item => {
            const { id, catalogName, premission, children } = item;
            const isOwner = premission === 'OWNER';
            const title = (
                <span style={{ fontSize: 13, color: isOwner ? undefined : '#999' }}>
                    {catalogName}
                    {!isOwner && <span style={{ marginLeft: 8, fontSize: 11, color: '#ccc' }}>（无权限）</span>}
                </span>
            );
            if (children && children.length > 0) {
                return (
                    <TreeNode key={String(id)} title={title} premission={premission} selectable={isOwner}>
                        {this.renderTreeNodes(children)}
                    </TreeNode>
                );
            }
            return <TreeNode key={String(id)} title={title} premission={premission} selectable={isOwner} />;
        });
    };

    render() {
        const {
            visible, loading, treeLoading, treeData,
            expandedKeys, autoExpandParent, selectedKeys,
            mode, sourceName,
        } = this.state;

        const title = mode === 'folder' ? '移动文件夹' : '移动文件';

        return (
            <Modal
                title={title}
                visible={visible}
                onOk={this.handleOk}
                onCancel={this.handleCancel}
                confirmLoading={loading}
                okText="确认移动"
                cancelText="取消"
                width={480}
                bodyStyle={{ maxHeight: 500, overflowY: 'auto' }}
            >
                <div>
                    <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                        请选择目标目录（仅显示有Owner权限的目录）：
                    </div>
                    <div
                        style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            padding: '8px 12px',
                            minHeight: 120,
                            maxHeight: 320,
                            overflow: 'auto',
                            backgroundColor: '#fff',
                        }}
                    >
                        {treeLoading ? (
                            <div style={{ textAlign: 'center', paddingTop: 40 }}>
                                <Spin tip="加载目录中..." />
                            </div>
                        ) : treeData.length > 0 ? (
                            <Tree
                                expandedKeys={expandedKeys}
                                selectedKeys={selectedKeys}
                                autoExpandParent={autoExpandParent}
                                onExpand={this.onExpand}
                                onSelect={this.onSelect}
                            >
                                {this.renderTreeNodes(treeData)}
                            </Tree>
                        ) : (
                            <div style={{ color: '#999', textAlign: 'center', paddingTop: 20 }}>
                                暂无有权限的目录数据
                            </div>
                        )}
                    </div>
                    <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                        当前待移动{ mode === 'folder' ? '文件夹' : '文件' }：<strong>{sourceName || '未知'}</strong>
                    </div>
                </div>
            </Modal>
        );
    }
}

export default MoveFolderModal;



// WEBPACK FOOTER //
// ./src/components/documentManagement/departmentalDocuments/MoveFolderModal.js